import React, { useEffect, useState } from 'react';
import { Plus, MapPin, Trash2, X, Loader2, RefreshCw, Building2 } from 'lucide-react';
import { teamsApi, TeamRecord, TeamRegionRow } from '../services/creatorApi';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { toast } from 'sonner';
export function Regions() {
  const {
    currentWorkspace,
    refreshTeams
  } = useWorkspace();
  const [team, setTeam] = useState<TeamRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [newRegion, setNewRegion] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const fetchTeam = async () => {
    setLoading(true);
    try {
      const teamData = await teamsApi.findByTeamName(currentWorkspace.name);
      setTeam(teamData);
    } catch (err) {
      console.error('Failed to fetch team:', err);
      toast.error('Failed to load team configuration');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchTeam();
  }, [currentWorkspace.name]);
  const handleCreateTeam = async () => {
    setSaving(true);
    try {
      const teamId = teamsApi.generateTeamId(currentWorkspace.name);
      await teamsApi.create({
        Team_Name: currentWorkspace.name,
        Team_ID: teamId,
        Team_Regions: []
      });
      toast.success('Team configuration created');
      fetchTeam();
    } catch (err) {
      console.error('Failed to create team:', err);
      toast.error('Failed to create team configuration');
    } finally {
      setSaving(false);
    }
  };
  const handleAddRegion = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newRegion.trim() || !team) return;
    // Check if region already exists
    const existingRegions = team.Team_Regions || [];
    if (existingRegions.some(r => r.Region_Name === newRegion.trim())) {
      toast.error('Region already exists');
      return;
    }
    setSaving(true);
    try {
      const regionId = newRegion.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
      const updatedRegions: TeamRegionRow[] = [...existingRegions, {
        Region_Name: newRegion.trim()
      }];
      await teamsApi.update(team.ID, {
        Team_Regions: updatedRegions
      });
      toast.success('Region added successfully');
      setNewRegion('');
      setShowModal(false);
      fetchTeam();
      refreshTeams();
    } catch (err) {
      console.error('Failed to add region:', err);
      toast.error('Failed to add region');
    } finally {
      setSaving(false);
    }
  };
  const handleDeleteRegion = async (regionName: string) => {
    if (!team) return;
    setSaving(true);
    try {
      const updatedRegions = (team.Team_Regions || []).filter(r => r.Region_Name !== regionName);
      await teamsApi.update(team.ID, {
        Team_Regions: updatedRegions
      });
      toast.success('Region removed');
      setDeleteConfirm(null);
      fetchTeam();
      refreshTeams();
    } catch (err) {
      console.error('Failed to delete region:', err);
      toast.error('Failed to remove region');
    } finally {
      setSaving(false);
    }
  };
  const regions = team?.Team_Regions || [];
  if (loading) {
    return <div className="flex items-center justify-center min-h-[400px]" data-id="element-5629">
        <Loader2 className="h-8 w-8 text-blue-500 animate-spin" data-id="element-5630" />
      </div>;
  }
  // If no team configuration exists, show setup prompt
  if (!team) {
    return <div className="space-y-6" data-id="element-5631">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-5632">
          <div className="min-w-0" data-id="element-5633">
            <h1 className="text-2xl font-semibold text-gray-900" data-id="element-5634">Regions</h1>
            <p className="mt-1 text-sm text-gray-500" data-id="element-5635">
              Manage regions for {currentWorkspace.name}.
            </p>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-12 text-center" data-id="element-5636">
          <Building2 className="mx-auto h-12 w-12 text-gray-400" data-id="element-5637" />
          <h3 className="mt-4 text-lg font-medium text-gray-900" data-id="element-5638">
            Team Not Set Up Yet
          </h3>
          <p className="mt-2 text-sm text-gray-500 max-w-md mx-auto" data-id="element-5639">
            Before adding regions, you need to initialize the team
            configuration. This will set up your team's regions and settings.
          </p>
          <button onClick={handleCreateTeam} disabled={saving} className="mt-6 inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50" data-id="element-5640">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" data-id="element-5641" /> : <Plus className="h-4 w-4" data-id="element-5642" />}
            Initialize Team
          </button>
        </div>
      </div>;
  }
  return <div className="space-y-6" data-id="element-5643">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-5644">
        <div className="min-w-0" data-id="element-5645">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-5646">Regions</h1>
          <p className="mt-1 text-sm text-gray-500" data-id="element-5647">
            Manage regions for {currentWorkspace.name}. Regions are used to
            scope and filter data across modules.
          </p>
        </div>
        <div className="flex items-center gap-3" data-id="element-5648">
          <button onClick={fetchTeam} disabled={loading} className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md" title="Refresh" data-id="element-5649">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} data-id="element-5650" />
          </button>
          <button onClick={() => setShowModal(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 whitespace-nowrap flex-shrink-0" data-id="element-5651">
            <Plus className="h-4 w-4 flex-shrink-0" data-id="element-5652" />
            <span data-id="element-5653">Add Region</span>
          </button>
        </div>
      </div>

      {/* Team Info Card */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-5654">
        <div className="flex items-center gap-3" data-id="element-5655">
          <Building2 className="h-5 w-5 text-blue-600" data-id="element-5656" />
          <div data-id="element-5657">
            <div className="text-sm font-medium text-blue-900" data-id="element-5658">
              {team.Team_Name}
            </div>
            <div className="text-xs text-blue-700 font-mono" data-id="element-5659">
              ID: {team.Team_ID}
            </div>
          </div>
          <span className={`ml-auto px-2 py-0.5 rounded text-xs font-medium ${team.Status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`} data-id="element-5660">
            {team.Status}
          </span>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-5661">
        {regions.length === 0 ? <div className="p-12 text-center" data-id="element-5662">
            <MapPin className="mx-auto h-12 w-12 text-gray-400" data-id="element-5663" />
            <h3 className="mt-2 text-sm font-semibold text-gray-900" data-id="element-5664">
              No regions defined
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-5665">
              Add regions to segment data by geographic or logical areas.
            </p>
            <button onClick={() => setShowModal(true)} className="mt-4 inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700" data-id="element-5666">
              <Plus className="h-4 w-4" data-id="element-5667" />
              Add your first region
            </button>
          </div> : <div className="overflow-x-auto" data-id="element-5668">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-5669">
              <thead className="bg-gray-50" data-id="element-5670">
                <tr data-id="element-5671">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5672">
                    Region Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5673">
                    Region ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5674">
                    Team
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5675">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-5676">
                {regions.map(region => <tr key={region.Region_ID} className="hover:bg-gray-50" data-id="element-5677">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-5678">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-blue-50 text-blue-700 border border-blue-200" data-id="element-5679">
                        <MapPin className="h-3.5 w-3.5 flex-shrink-0" data-id="element-5680" />
                        {region.Region_Name}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-5681">
                      <code className="text-xs font-mono text-gray-500 bg-gray-100 px-2 py-1 rounded" data-id="element-5682">
                        {region.Region_ID}
                      </code>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-5683">
                      {team.Team_Name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-5684">
                      <button onClick={() => setDeleteConfirm(region.Region_Name)} className="text-red-600 hover:text-red-900 p-2 hover:bg-red-50 rounded" title="Delete" data-id="element-5685">
                        <Trash2 className="h-4 w-4" data-id="element-5686" />
                      </button>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>}
      </div>

      {/* Create Modal */}
      {showModal && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-5687">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-5688">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowModal(false)} data-id="element-5689" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md p-6" data-id="element-5690">
              <div className="flex items-center justify-between mb-4" data-id="element-5691">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-5692">
                  Add New Region
                </h3>
                <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-500" data-id="element-5693">
                  <X className="h-5 w-5" data-id="element-5694" />
                </button>
              </div>
              <form onSubmit={handleAddRegion} className="space-y-4" data-id="element-5695">
                <div data-id="element-5696">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5697">
                    Region Name
                  </label>
                  <div className="relative" data-id="element-5698">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" data-id="element-5699" />
                    <input type="text" required className="block w-full rounded-md border-gray-300 pl-10 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={newRegion} onChange={e => setNewRegion(e.target.value)} placeholder="e.g., Sao Paulo, North, Retail Team" data-id="element-5700" />
                  </div>
                </div>
                <div data-id="element-5701">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5702">
                    Team
                  </label>
                  <input type="text" className="block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm sm:text-sm text-gray-500 cursor-not-allowed" value={team.Team_Name} disabled data-id="element-5703" />
                </div>
                <div className="flex justify-end gap-3 pt-4" data-id="element-5704">
                  <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-5705">
                    Cancel
                  </button>
                  <button type="submit" disabled={!newRegion.trim() || saving} className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2" data-id="element-5706">
                    {saving && <Loader2 className="h-4 w-4 animate-spin" data-id="element-5707" />}
                    Add Region
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>}

      {/* Delete Confirmation */}
      {deleteConfirm && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-5708">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-5709">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setDeleteConfirm(null)} data-id="element-5710" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-sm p-6" data-id="element-5711">
              <div className="flex items-center gap-4 mb-4" data-id="element-5712">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center" data-id="element-5713">
                  <Trash2 className="h-5 w-5 text-red-600" data-id="element-5714" />
                </div>
                <div data-id="element-5715">
                  <h3 className="text-lg font-medium text-gray-900" data-id="element-5716">
                    Remove Region
                  </h3>
                  <p className="text-sm text-gray-500" data-id="element-5717">
                    Remove "{deleteConfirm}" from {team.Team_Name}? This won't
                    delete CRM records tagged with this region.
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3" data-id="element-5718">
                <button onClick={() => setDeleteConfirm(null)} disabled={saving} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-5719">
                  Cancel
                </button>
                <button onClick={() => handleDeleteRegion(deleteConfirm)} disabled={saving} className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center gap-2" data-id="element-5720">
                  {saving && <Loader2 className="h-4 w-4 animate-spin" data-id="element-5721" />}
                  Remove
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}